

import React, { useState, useEffect, useRef } from 'react';
import TextInput from './TextInput';
import TextAreaInput from './TextAreaInput';
import RadioInput from './RadioInput';
import CheckboxInput from './CheckBoxInput';
import RatingInput from './RatingInput';
import DateInput from './DateInput';
import SignatureInput from './Signatureinput';
import TableInput from './Tableinput';
import CalculatedInput from './Calculatedinput';
import NavigationButtons from './NavigationButtons';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import { useFormValidation } from './Hooks/useFormValidation';
import SingleCheckboxInput from './SingleCheckboxInput';
import styles from './DynamicFormRenderer.module.css';
import SelectInput from './Selectinput';
import { upload, deleteFiles } from '../../../../Config/api';
import InfoInput from '../InfoInput';

const DynamicFormRenderer = ({
    section,
    data,
    onUpdate,
    onNext,
    onBack,
    isLastSection,
    isSubmitting,
    isSubmitted,
    onEligibilityConfirm,
    allSections,
    onNavigateToSection,
    isEligible,
    currentSectionIndex,
    userRole = '',
    isCRO = false,
    isViewOnly = false,
    isEditMode = false
}) => {
    const { errors, setErrors } = useFormValidation();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showExclusionModal, setShowExclusionModal] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const previousDataRef = useRef({});
    const fieldRefs = useRef({});
    
    // State for Reason for Change modal
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingChange, setPendingChange] = useState(null);
    const [changeReason, setChangeReason] = useState('');
    
    // Store original values when in edit mode (for tracking changes)
    const originalDataRef = useRef({});
    const [pendingFieldChanges, setPendingFieldChanges] = useState({});
    const [isOriginalDataInitialized, setIsOriginalDataInitialized] = useState(false);
    const isFirstRenderRef = useRef(true);

    // Initialize original data when in edit mode (editing a previously submitted form)
    // Only initialize if data was already populated on first render (meaning it was loaded from API)
    // This prevents tracking changes when user fills a fresh form that was never submitted
    useEffect(() => {
        if (isEditMode && !isOriginalDataInitialized) {
            // Only initialize on first render when data already has values (loaded from API)
            if (isFirstRenderRef.current && Object.keys(data).length > 0) {
                originalDataRef.current = { ...data };
                setIsOriginalDataInitialized(true);
                console.log('Original data initialized for edit mode:', originalDataRef.current);
            }
            isFirstRenderRef.current = false;
        }
    }, [isEditMode, data, isOriginalDataInitialized]);

    const isEligibilitySection = section.id === 'section_1_eligibility';
    const isFirstSection = currentSectionIndex === 0;

    useEffect(() => {
        if (isFirstSection && isEligibilitySection) {
            checkEligibility();
        }
    }, [data, isFirstSection, isEligibilitySection]);

    const checkEligibility = () => {
        const inclusionSubsection = section.subsections?.find(sub => sub.id === 'inclusion_criteria');
        const exclusionSubsection = section.subsections?.find(sub => sub.id === 'exclusion_criteria');

        if (!inclusionSubsection || !exclusionSubsection) return;

        const inclusionField = inclusionSubsection.fields?.find(f => f.id === 'inclusion_eligible');
        const exclusionField = exclusionSubsection.fields?.find(f => f.id === 'exclusion_eligible');

        if (!inclusionField || !exclusionField) return;

        const inclusionValue = data[inclusionField.id];
        const exclusionValue = data[exclusionField.id];

        const previousInclusionValue = previousDataRef.current[inclusionField.id];
        const previousExclusionValue = previousDataRef.current[exclusionField.id];

        const inclusionChanged = inclusionValue !== previousInclusionValue;
        const exclusionChanged = exclusionValue !== previousExclusionValue;

        if (inclusionChanged && inclusionValue === 'NO') {
            setShowExclusionModal(true);
        }

        if (exclusionChanged && exclusionValue === 'YES') {
            setShowExclusionModal(true);
        }

        if (inclusionChanged && inclusionValue === 'YES') {
            const exclusionValid = exclusionValue === 'NO';
            if (exclusionValid && onEligibilityConfirm) {
                onEligibilityConfirm(true);
            }
        }

        if (exclusionChanged && exclusionValue === 'NO') {
            const inclusionValid = inclusionValue === 'YES';
            if (inclusionValid && onEligibilityConfirm) {
                onEligibilityConfirm(true);
            }
        }

        previousDataRef.current[inclusionField.id] = inclusionValue;
        previousDataRef.current[exclusionField.id] = exclusionValue;
    };

    const scrollToFirstError = (firstErrorFieldId) => {
        // Try multiple methods to find the element
        let element = fieldRefs.current[firstErrorFieldId];
        
        if (!element) {
            element = document.querySelector(`[data-field-id="${firstErrorFieldId}"]`);
        }
        
        if (!element) {
            element = document.getElementById(`field-${firstErrorFieldId}`);
        }
        
        if (!element) {
            const input = document.querySelector(`input[name="${firstErrorFieldId}"], select[name="${firstErrorFieldId}"], textarea[name="${firstErrorFieldId}"]`);
            if (input) {
                element = input.closest('[data-field-id]') || input.parentElement?.parentElement;
            }
        }

        if (element) {
            // Get the element's position
            const rect = element.getBoundingClientRect();
            const absoluteTop = rect.top + window.scrollY;
            
            // Scroll with offset for header
            const offset = 120;
            const scrollPosition = absoluteTop - offset;
            
            // Scroll to the element
            window.scrollTo({
                top: Math.max(0, scrollPosition),
                behavior: 'smooth'
            });

            // Focus input after scroll completes
            setTimeout(() => {
                const input = element.querySelector('input:not([type="hidden"]), textarea, select');
                if (input) {
                    input.focus();
                }
            }, 500);
        } else {
            // Fallback: scroll to top of form
            const formCard = document.querySelector('[class*="formCard"]');
            if (formCard) {
                formCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const validateTableRows = (field, tableData) => {
        const tableErrors = [];
        let hasTableError = false;

        if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
            return { hasError: true, message: 'At least one row is required' };
        }

        tableData.forEach((row, rowIndex) => {
            const rowErrors = {};
            field.columns.forEach(column => {
                if (column.required) {
                    const cellValue = row[column.id];
                    const isEmpty = cellValue === undefined || cellValue === null || cellValue === '';
                    if (isEmpty) {
                        rowErrors[column.id] = 'Required';
                        hasTableError = true;
                    }
                }
            });
            if (Object.keys(rowErrors).length > 0) {
                tableErrors.push({ rowIndex, errors: rowErrors });
            }
        });

        return { hasError: hasTableError, rowErrors: tableErrors };
    };

    const validateCurrentSection = () => {
        if (isEligible === false && !isFirstSection && !isLastSection) {
            setErrors({});
            setValidationMessage('');
            return true;
        }

        const newErrors = {};
        let isValid = true;
        const missingFields = [];
        let firstErrorFieldId = null;

        const validateFields = (fields) => {
            if (!fields) return;

            fields.forEach(field => {
                let isVisible = true;
                if (field.showIf) {
                    const { field: conditionField, value: conditionValue } = field.showIf;
                    const actualValue = data[conditionField];

                    if (typeof conditionValue === 'boolean') {
                        isVisible = actualValue === conditionValue;
                    } else {
                        isVisible = actualValue === conditionValue;
                    }
                }

                if (!isVisible) return;

                if (field.type === 'table' && field.required) {
                    const tableData = data[field.id];
                    const tableValidation = validateTableRows(field, tableData);
                    
                    if (tableValidation.hasError) {
                        newErrors[field.id] = tableValidation.message || 'Please fill all required fields in the table';
                        missingFields.push(field.label || 'Medication Log');
                        if (!firstErrorFieldId) {
                            firstErrorFieldId = field.id;
                        }
                        isValid = false;
                    }
                    return;
                }

                if (field.required) {
                    const value = data[field.id];
                    const isEmpty = value === undefined ||
                        value === null ||
                        value === '' ||
                        (Array.isArray(value) && value.length === 0);

                    if (isEmpty) {
                        newErrors[field.id] = 'This field is required';
                        missingFields.push(field.label || field.id);
                        if (!firstErrorFieldId) {
                            firstErrorFieldId = field.id;
                        }
                        isValid = false;
                    }
                }

                // Validate linkedField for checkboxes
                if (field.type === 'checkbox' && !field.options && field.linkedField) {
                    const isChecked = data[field.id] === true || data[field.id] === 'true' || data[field.id] === 'YES';
                    
                    if (isChecked) {
                        const linkedFieldId = field.linkedField.id;
                        const linkedValue = data[linkedFieldId];
                        const linkedEmpty = linkedValue === undefined || linkedValue === null || linkedValue === '';
                        
                        if (linkedEmpty) {
                            newErrors[linkedFieldId] = 'Duration is required when condition is selected';
                            missingFields.push(field.linkedField.label || 'Duration');
                            if (!firstErrorFieldId) {
                                firstErrorFieldId = linkedFieldId;
                            }
                            isValid = false;
                        }
                    }
                }

                // Legacy support for duration fields without linkedField structure
                if (field.type === 'checkbox' && !field.options && !field.linkedField) {
                    const isChecked = data[field.id] === true || data[field.id] === 'true' || data[field.id] === 'YES';
                    
                    if (isChecked) {
                        const durationFieldId = `${field.id}_duration_years`;
                        const durationField = fields.find(f => f.id === durationFieldId);
                        
                        if (durationField) {
                            const durationValue = data[durationFieldId];
                            const durationEmpty = durationValue === undefined || 
                                                   durationValue === null || 
                                                   durationValue === '';
                            
                            if (durationEmpty) {
                                newErrors[durationFieldId] = 'Duration is required when condition is selected';
                                missingFields.push(durationField.label || 'Duration');
                                if (!firstErrorFieldId) {
                                    firstErrorFieldId = durationFieldId;
                                }
                                isValid = false;
                            }
                        }
                    }
                }

                if (field.type === 'number' && data[field.id]) {
                    const value = data[field.id];
                    
                    if (field.validation?.pattern === 'numeric') {
                        const numericRegex = /^[0-9]*$/;
                        if (!numericRegex.test(String(value))) {
                            newErrors[field.id] = 'Only numbers allowed';
                            if (!firstErrorFieldId) {
                                firstErrorFieldId = field.id;
                            }
                            isValid = false;
                            return;
                        }
                    }

                    const numValue = parseFloat(value);

                    if (isNaN(numValue)) {
                        newErrors[field.id] = 'Please enter a valid number';
                        if (!firstErrorFieldId) {
                            firstErrorFieldId = field.id;
                        }
                        isValid = false;
                        return;
                    }

                    if (field.validation?.min !== undefined && numValue < field.validation.min) {
                        newErrors[field.id] = `Minimum value is ${field.validation.min}`;
                        if (!firstErrorFieldId) {
                            firstErrorFieldId = field.id;
                        }
                        isValid = false;
                    }
                    if (field.validation?.max !== undefined && numValue > field.validation.max) {
                        newErrors[field.id] = `Maximum value is ${field.validation.max}`;
                        if (!firstErrorFieldId) {
                            firstErrorFieldId = field.id;
                        }
                        isValid = false;
                    }

                    if (field.validation?.maxLength) {
                        const integerPart = String(value).split('.')[0];
                        if (integerPart.length > field.validation.maxLength) {
                            newErrors[field.id] = `Maximum ${field.validation.maxLength} digits allowed`;
                            if (!firstErrorFieldId) {
                                firstErrorFieldId = field.id;
                            }
                            isValid = false;
                        }
                    }

                    if (field.validation?.allowDecimal && field.validation?.decimalPlaces) {
                        const decimalPart = String(value).split('.')[1];
                        if (decimalPart && decimalPart.length > field.validation.decimalPlaces) {
                            newErrors[field.id] = `Maximum ${field.validation.decimalPlaces} decimal places allowed`;
                            if (!firstErrorFieldId) {
                                firstErrorFieldId = field.id;
                            }
                            isValid = false;
                        }
                    }
                }

                if (field.type === 'text' && data[field.id] && field.validation?.pattern === 'alphanumericDecimal') {
                    const value = data[field.id];
                    const regex = /^[0-9.]*$/;
                    if (!regex.test(value)) {
                        newErrors[field.id] = 'Only numbers and decimal points allowed';
                        if (!firstErrorFieldId) {
                            firstErrorFieldId = field.id;
                        }
                        isValid = false;
                    }
                }
            });
        };

        if (section.subsections) {
            section.subsections.forEach(subsection => {
                if (subsection.fields) {
                    validateFields(subsection.fields);
                    
                    if (subsection.id === 'medical_history' || subsection.id === 'family_history') {
                        const hasMedicalHistoryField = subsection.fields.find(f => f.id === 'has_medical_history');
                        const hasFamilyHistoryField = subsection.fields.find(f => f.id === 'has_family_history');
                        
                        const relevantField = hasMedicalHistoryField || hasFamilyHistoryField;
                        
                        if (relevantField && data[relevantField.id] === 'YES') {
                            const checkboxFields = subsection.fields.filter(f => 
                                f.type === 'checkbox' && 
                                f.showIf && 
                                f.showIf.field === relevantField.id &&
                                f.showIf.value === 'YES'
                            );
                            
                            const hasAtLeastOneChecked = checkboxFields.some(field => 
                                data[field.id] === true || 
                                data[field.id] === 'true' || 
                                data[field.id] === 'YES'
                            );
                            
                            if (!hasAtLeastOneChecked) {
                                newErrors[relevantField.id] = 'Please select at least one medical condition';
                                if (!firstErrorFieldId) {
                                    firstErrorFieldId = relevantField.id;
                                }
                                isValid = false;
                            }
                        }
                    }
                }
            });
        }

        setErrors(newErrors);

        if (!isValid) {
            if (missingFields.length > 0) {
                setValidationMessage(`Please fill required fields: ${missingFields.slice(0, 3).join(', ')}${missingFields.length > 3 ? '...' : ''}`);
            } else {
                // Get the error message from the first error
                const firstErrorMessage = newErrors[firstErrorFieldId];
                setValidationMessage(firstErrorMessage || 'Please correct the errors below');
            }
            
            if (firstErrorFieldId) {
                // Increase timeout to ensure DOM is updated
                setTimeout(() => scrollToFirstError(firstErrorFieldId), 200);
            }
        } else {
            setValidationMessage('');
        }

        return isValid;
    };
    
    const getCurrentEligibilityStatus = () => {
        const inclusionSubsection = section.subsections?.find(sub => sub.id === 'inclusion_criteria');
        const exclusionSubsection = section.subsections?.find(sub => sub.id === 'exclusion_criteria');

        if (!inclusionSubsection || !exclusionSubsection) return null;

        const inclusionField = inclusionSubsection.fields?.find(f => f.id === 'inclusion_eligible');
        const exclusionField = exclusionSubsection.fields?.find(f => f.id === 'exclusion_eligible');

        if (!inclusionField || !exclusionField) return null;

        const inclusionAnswered = data[inclusionField.id] !== undefined;
        const exclusionAnswered = data[exclusionField.id] !== undefined;

        if (!inclusionAnswered || !exclusionAnswered) {
            return null;
        }

        const inclusionValid = data[inclusionField.id] === 'YES';
        const exclusionValid = data[exclusionField.id] === 'NO';

        return inclusionValid && exclusionValid;
    };

    // Get field label by id
    const getFieldLabel = (fieldId) => {
        let label = fieldId;
        section.subsections?.forEach(subsection => {
            const field = subsection.fields?.find(f => f.id === fieldId);
            if (field && field.label) {
                label = field.label;
            }
        });
        return label;
    };

    // Apply the change after reason is provided
    const applyFieldChange = (fieldId, value, reason) => {
        const updates = { [fieldId]: value };

        const allFields = [];
        section.subsections?.forEach(subsection => {
            if (subsection.fields) {
                allFields.push(...subsection.fields);
            }
        });

        const clearDependentFields = (parentFieldId, parentValue) => {
            const dependentFields = allFields.filter(field => {
                if (!field.showIf) return false;
                return field.showIf.field === parentFieldId;
            });

            dependentFields.forEach(depField => {
                const shouldShow = typeof depField.showIf.value === 'boolean' 
                    ? (parentValue === true || parentValue === 'true' || parentValue === 'YES')
                    : parentValue === depField.showIf.value;

                if (!shouldShow) {
                    if (depField.type === 'table') {
                        updates[depField.id] = [];
                    } else if (depField.type === 'checkbox' && depField.options) {
                        updates[depField.id] = [];
                    } else {
                        updates[depField.id] = null;
                    }

                    if (depField.linkedField) {
                        updates[depField.linkedField.id] = null;
                    }

                    clearDependentFields(depField.id, null);
                }
            });
        };

        clearDependentFields(fieldId, value);

        const field = allFields.find(f => f.id === fieldId);
        if (field && field.type === 'checkbox' && field.linkedField && !value) {
            updates[field.linkedField.id] = null;
        }

        // Auto-selection logic for Medical History based on vital signs and BMI
        const currentData = { ...data, ...updates };

        // Check for Blood Pressure Systolic and Diastolic
        // Auto-check Hypertension if SBP >= 140 OR DBP >= 90
        if (fieldId === 'blood_pressure_systolic' || fieldId === 'blood_pressure_diastolic') {
            const systolic = parseFloat(fieldId === 'blood_pressure_systolic' ? value : currentData.blood_pressure_systolic);
            const diastolic = parseFloat(fieldId === 'blood_pressure_diastolic' ? value : currentData.blood_pressure_diastolic);
            
            const hasHighSystolic = !isNaN(systolic) && systolic >= 140;
            const hasHighDiastolic = !isNaN(diastolic) && diastolic >= 90;
            
            if (hasHighSystolic || hasHighDiastolic) {
                // Auto-select hypertension when BP is high (SBP >= 140 OR DBP >= 90)
                updates.hypertension = true;
                // Also set has_medical_history to YES to show the checkboxes
                updates.has_medical_history = 'YES';
            } else if (!isNaN(systolic) && !isNaN(diastolic)) {
                // Only auto-deselect hypertension when both values are present and normal
                updates.hypertension = false;
                // Also clear the duration field if it exists
                updates.hypertension_duration_years = null;
            }
        }

        // Check for BMI - If BMI > 25, auto-select obesity; if BMI <= 25, auto-deselect obesity
        if (fieldId === 'height' || fieldId === 'weight' || fieldId === 'bmi') {
            let bmiValue;
            
            if (fieldId === 'bmi') {
                bmiValue = parseFloat(value);
            } else {
                const height = parseFloat(fieldId === 'height' ? value : currentData.height);
                const weight = parseFloat(fieldId === 'weight' ? value : currentData.weight);
                
                if (!isNaN(height) && !isNaN(weight) && height > 0) {
                    bmiValue = weight / Math.pow(height / 100, 2);
                }
            }
            
            if (!isNaN(bmiValue)) {
                if (bmiValue > 25) {
                    // Auto-select obesity when BMI > 25
                    updates.obesity = true;
                    // Also set has_medical_history to YES to show the checkboxes
                    updates.has_medical_history = 'YES';
                } else {
                    // Auto-deselect obesity when BMI <= 25
                    updates.obesity = false;
                    // Also clear the duration field if it exists
                    updates.obesity_duration_years = null;
                }
            }
        }

        // Store the change reason in a special field for audit trail
        if (reason) {
            const changeLog = data._changeLog || [];
            changeLog.push({
                fieldId,
                fieldLabel: getFieldLabel(fieldId),
                oldValue: data[fieldId],
                newValue: value,
                reason,
                timestamp: new Date().toISOString()
            });
            updates._changeLog = changeLog;
        }

        onUpdate(updates);

        // Clear errors for this field
        if (errors[fieldId]) {
            const newErrors = { ...errors };
            delete newErrors[fieldId];
            
            Object.keys(updates).forEach(key => {
                if (key !== fieldId) {
                    delete newErrors[key];
                }
            });
            
            setErrors(newErrors);
            setValidationMessage('');
        }
    };

    const handleFieldChange = (fieldId, value) => {
        // For view-only, block changes
        if (isViewOnly) return;

        // If in edit mode, track the change but allow it
        // The reason modal will be shown on blur only if original value was not empty
        if (isEditMode && isOriginalDataInitialized) {
            const originalValue = originalDataRef.current[fieldId];
            
            // Check if original value was empty (null, undefined, empty string, or empty array)
            const isOriginalEmpty = originalValue === null || 
                                    originalValue === undefined || 
                                    originalValue === '' ||
                                    (Array.isArray(originalValue) && originalValue.length === 0);
            
            // Only track changes if original value was NOT empty
            if (!isOriginalEmpty && originalValue !== value) {
                setPendingFieldChanges(prev => ({
                    ...prev,
                    [fieldId]: { oldValue: originalValue, newValue: value }
                }));
                console.log('Change tracked:', fieldId, 'from', originalValue, 'to', value);
            } else {
                // If value is back to original OR original was empty, remove from pending
                setPendingFieldChanges(prev => {
                    const updated = { ...prev };
                    delete updated[fieldId];
                    return updated;
                });
            }
        }

        applyFieldChange(fieldId, value, null);
    };

    // Handle field blur - show reason modal if there are pending changes
    // For non-text inputs (radio, select, checkbox, etc.), change info is passed directly
    // because React state update is async and might not be ready when blur is called
    const handleFieldBlur = (fieldId, changeInfo = null) => {
        console.log('handleFieldBlur called for:', fieldId, 'isEditMode:', isEditMode, 'isViewOnly:', isViewOnly, 'changeInfo:', changeInfo, 'pending:', pendingFieldChanges[fieldId]);
        
        if (!isEditMode || isViewOnly) return;
        
        // Use changeInfo if provided (for immediate triggers like radio/select/checkbox)
        // Otherwise fall back to pendingFieldChanges (for text inputs with native blur)
        const change = changeInfo || pendingFieldChanges[fieldId];
        
        if (change) {
            setPendingChange({ 
                fieldId, 
                newValue: change.newValue, 
                oldValue: change.oldValue 
            });
            setChangeReason('');
            setShowReasonModal(true);
            console.log('Showing reason modal for field:', fieldId);
        }
    };

    // Helper function to handle immediate change + blur for non-text inputs (radio, select, checkbox, date, rating)
    const handleImmediateFieldChange = (fieldId, value) => {
        // For view-only, block changes
        if (isViewOnly) return;

        let changeInfo = null;

        // If in edit mode, check if we need to show reason modal
        if (isEditMode && isOriginalDataInitialized) {
            const originalValue = originalDataRef.current[fieldId];
            
            // Check if original value was empty (null, undefined, empty string, or empty array)
            const isOriginalEmpty = originalValue === null || 
                                    originalValue === undefined || 
                                    originalValue === '' ||
                                    (Array.isArray(originalValue) && originalValue.length === 0);
            
            // Only track changes if original value was NOT empty and value is different
            if (!isOriginalEmpty && originalValue !== value) {
                changeInfo = { oldValue: originalValue, newValue: value };
                setPendingFieldChanges(prev => ({
                    ...prev,
                    [fieldId]: changeInfo
                }));
                console.log('Immediate change tracked:', fieldId, 'from', originalValue, 'to', value);
            } else {
                // If value is back to original OR original was empty, remove from pending
                setPendingFieldChanges(prev => {
                    const updated = { ...prev };
                    delete updated[fieldId];
                    return updated;
                });
            }
        }

        applyFieldChange(fieldId, value, null);

        // Immediately show reason modal if there's a change to track
        if (changeInfo) {
            setTimeout(() => handleFieldBlur(fieldId, changeInfo), 100);
        }
    };

    // Handle reason modal submit
    const handleReasonSubmit = () => {
        if (pendingChange && changeReason.trim()) {
            // Log the change with reason
            const changeLog = data._changeLog || [];
            changeLog.push({
                fieldId: pendingChange.fieldId,
                fieldLabel: getFieldLabel(pendingChange.fieldId),
                oldValue: pendingChange.oldValue,
                newValue: pendingChange.newValue,
                reason: changeReason,
                timestamp: new Date().toISOString()
            });
            onUpdate({ _changeLog: changeLog });
            
            // Update original data ref so subsequent changes are tracked correctly
            originalDataRef.current[pendingChange.fieldId] = pendingChange.newValue;
            
            // Remove from pending changes
            setPendingFieldChanges(prev => {
                const updated = { ...prev };
                delete updated[pendingChange.fieldId];
                return updated;
            });
            
            setShowReasonModal(false);
            setPendingChange(null);
            setChangeReason('');
        }
    };

    // Handle reason modal cancel - restore original value
    const handleReasonCancel = () => {
        if (pendingChange) {
            // Restore the original value
            onUpdate({ [pendingChange.fieldId]: pendingChange.oldValue });
            
            // Remove from pending changes
            setPendingFieldChanges(prev => {
                const updated = { ...prev };
                delete updated[pendingChange.fieldId];
                return updated;
            });
        }
        setShowReasonModal(false);
        setPendingChange(null);
        setChangeReason('');
    };

    const handleNext = () => {
        // Skip validation for view-only users (CRO, Sponsor, etc.) - they should just navigate freely
        if (isViewOnly) {
            onNext();
            return;
        }

        const isValid = validateCurrentSection();

        if (!isValid) {
            return;
        }

        if (isEligibilitySection) {
            const currentEligibilityStatus = getCurrentEligibilityStatus();

            if (currentEligibilityStatus === true) {
                onNext();
                return;
            } else if (currentEligibilityStatus === false) {
                if (onEligibilityConfirm) {
                    onEligibilityConfirm(false);
                }
                if (allSections && onNavigateToSection) {
                    const lastSectionIndex = allSections.length - 1;
                    onNavigateToSection(lastSectionIndex);
                }
                return;
            }
        }

        if (isLastSection) {
            setShowConfirmation(true);
        } else {
            onNext();
        }
    };

    const handleExclusionConfirm = () => {
        setShowExclusionModal(false);

        if (onEligibilityConfirm) {
            onEligibilityConfirm(false);
        }

        if (allSections && onNavigateToSection) {
            const lastSectionIndex = allSections.length - 1;
            onNavigateToSection(lastSectionIndex);
        }
    };

    const handleExclusionCancel = () => {
        setShowExclusionModal(false);
    };

    const handleConfirmSubmit = () => {
        setShowConfirmation(false);
        onNext();
    };

    const handleCancelSubmit = () => {
        setShowConfirmation(false);
    };

    const handleMultiSelect = (fieldId, value, checked) => {
        if (isSubmitted) return;

        const current = data[fieldId] || [];
        const updated = checked ? [...current, value] : current.filter(v => v !== value);
        handleImmediateFieldChange(fieldId, updated);
    };

    const handleSignatureUpload = async (fieldId, signatureData) => {
        try {
            // First, save the signature data locally immediately
            // This ensures the signature is captured even if upload fails
            onUpdate({
                [fieldId]: signatureData
            });

            // Clear any existing error for this field
            if (errors[fieldId]) {
                const newErrors = { ...errors };
                delete newErrors[fieldId];
                setErrors(newErrors);
            }

            // Then try to upload to server
            const blob = await fetch(signatureData).then(r => r.blob());
            const file = new File([blob], `signature_${fieldId}.png`, { type: 'image/png' });

            const formData = new FormData();
            formData.append('file', file);
            formData.append('fieldId', fieldId);

            const response = await upload(formData);

            if (response.data) {
                // Update with the server URL
                onUpdate({
                    [fieldId]: signatureData,
                    [`${fieldId}_url`]: response.data.url || response.data.filePath
                });
            }

            return response;
        } catch (error) {
            console.error('Error uploading signature:', error);
            // Even if upload fails, the signature data is already saved locally
            // Don't throw - just log the error
        }
    };

    const handleSignatureDelete = async (fieldId) => {
        try {
            const fileUrl = data[`${fieldId}_url`];

            if (fileUrl) {
                await deleteFiles({
                    fileUrl: fileUrl,
                    fieldId: fieldId
                });
            }

            onUpdate({
                [fieldId]: null,
                [`${fieldId}_url`]: null
            });
        } catch (error) {
            console.error('Error deleting signature:', error);
            throw error;
        }
    };
    
    // Skip duration fields that are now handled via linkedField
    const shouldSkipField = (field, subsectionIndex, fieldIndex) => {
        if (field.id.endsWith('_duration_years')) {
            const currentSubsection = section.subsections[subsectionIndex];
            const prevFieldIndex = fieldIndex - 1;
            const prevField = currentSubsection?.fields?.[prevFieldIndex];

            // Skip if previous field is a checkbox with linkedField
            if (prevField && prevField.type === 'checkbox' && prevField.linkedField &&
                field.id === prevField.linkedField.id) {
                return true;
            }

            // Legacy support: skip if it matches the old pattern
            if (prevField && prevField.type === 'checkbox' &&
                field.id === `${prevField.id}_duration_years`) {
                return true;
            }
        }
        return false;
    };

    const shouldShowConditional = (field) => {
        if (!field || !field.showIf) {
            return true;
        }

        const { field: conditionField, value: conditionValue } = field.showIf;
        const actualValue = data[conditionField];

        if (typeof conditionValue === 'boolean') {
            return actualValue === true || actualValue === 'true' || actualValue === 'YES';
        }

        return actualValue === conditionValue;
    };
    
    const setFieldRef = (fieldId, element) => {
        if (element) {
            fieldRefs.current[fieldId] = element;
        }
    };

    const getVisibleFieldIndex = (subsections, currentSubsectionIndex, currentFieldIndex) => {
        let visibleIndex = 0;

        for (let subIdx = 0; subIdx < subsections.length; subIdx++) {
            const subsection = subsections[subIdx];
            if (!subsection.fields) continue;

            for (let fieldIdx = 0; fieldIdx < subsection.fields.length; fieldIdx++) {
                const field = subsection.fields[fieldIdx];

                if (field.type === 'info') continue;

                const shouldShow = shouldShowConditional(field);

                if (subIdx === currentSubsectionIndex && fieldIdx === currentFieldIndex) {
                    return shouldShow ? visibleIndex : -1;
                }

                if (shouldShow) {
                    visibleIndex++;
                }
            }
        }

        return -1;
    };

    const renderField = (field, subsectionIndex, fieldIndex) => {
        const shouldShow = shouldShowConditional(field);

        if (!shouldShow) {
            return null;
        }

        const value = data[field.id];
        const error = errors[field.id];
        
        // Debug: log when rendering fields with data
        if (value !== undefined && value !== null && value !== '') {
            console.log(`Field ${field.id} has value:`, value);
        }

        const visibleIndex = getVisibleFieldIndex(section.subsections, subsectionIndex, fieldIndex);

        const question = {
            id: field.id,
            type: field.type,
            text: field.label,
            label: field.label,
            required: field.required,
            options: field.options,
            validation: field.validation,
            formula: field.formula,
            dependencies: field.dependencies,
            columns: field.columns,
            showIf: field.showIf,
            linkedField: field.linkedField
        };

        const commonProps = {
            question,
            index: visibleIndex,
            value,
            error,
            isSubmitted,
            data,
            disabled: isViewOnly,
            onBlur: () => handleFieldBlur(field.id)
        };

        // Determine field width class
        const getFieldWidthClass = () => {
            if (field.width === 50) return styles.fieldWidth50;
            if (field.width === 33) return styles.fieldWidth33;
            if (field.width === 66) return styles.fieldWidth66;
            return styles.fieldWidthFull;
        };

        const fieldWrapper = (component, customClass = null) => (
            <div
                key={field.id}
                ref={(el) => setFieldRef(field.id, el)}
                className={customClass || getFieldWidthClass()}
                data-field-id={field.id}
                id={`field-${field.id}`}
            >
                {component}
            </div>
        );

        switch (field.type) {
            case 'info':
                return fieldWrapper(
                    <InfoInput
                        question={{
                            id: field.id,
                            items: field.items,
                            variant: field.variant,
                            content: field.content
                        }}
                    />
                );

            case 'text':
            case 'number':
                return fieldWrapper(
                    <TextInput
                        {...commonProps}
                        onChange={(val) => handleFieldChange(field.id, val)}
                    />
                );

            case 'textarea':
                return fieldWrapper(
                    <TextAreaInput
                        {...commonProps}
                        onChange={(val) => handleFieldChange(field.id, val)}
                    />
                );

            case 'date':
                return fieldWrapper(
                    <DateInput
                        {...commonProps}
                        onBlur={undefined}
                        onChange={(val) => handleImmediateFieldChange(field.id, val)}
                    />
                );

            case 'select':
                return fieldWrapper(
                    <SelectInput
                        {...commonProps}
                        onBlur={undefined}
                        onChange={(val) => handleImmediateFieldChange(field.id, val)}
                    />
                );

            case 'radio':
                return fieldWrapper(
                    <RadioInput
                        {...commonProps}
                        onBlur={undefined}
                        onChange={(val) => handleImmediateFieldChange(field.id, val)}
                        onOtherChange={(val) => handleFieldChange(`${field.id}_other`, val)}
                    />
                );

            case 'checkbox':
                if (field.options && Array.isArray(field.options)) {
                    return fieldWrapper(
                        <CheckboxInput
                            {...commonProps}
                            onMultiSelect={handleMultiSelect}
                        />
                    );
                } else {
                    // Single checkbox - check if it has linkedField (new structure)
                    if (field.linkedField) {
                        const isChecked = value === true || value === 'true' || value === 'YES';
                        const linkedFieldValue = data[field.linkedField.id];
                        const linkedFieldError = errors[field.linkedField.id];

                        return (
                            <div
                                key={field.id}
                                ref={(el) => setFieldRef(field.id, el)}
                                className={styles.fieldWidthFull}
                                data-field-id={field.id}
                                id={`field-${field.id}`}
                            >
                                <SingleCheckboxInput
                                    question={question}
                                    value={value}
                                    error={error}
                                    isSubmitted={isSubmitted}
                                    onChange={(val) => handleImmediateFieldChange(field.id, val)}
                                    linkedField={field.linkedField}
                                    linkedFieldValue={linkedFieldValue}
                                    linkedFieldError={linkedFieldError}
                                    onLinkedFieldChange={(val) => handleFieldChange(field.linkedField.id, val)}
                                    onBlur={undefined}
                                    disabled={isViewOnly}
                                />
                            </div>
                        );
                    }

                    // Legacy support: check for separate duration field
                    const currentSubsection = section.subsections[subsectionIndex];
                    const nextFieldIndex = fieldIndex + 1;
                    const nextField = currentSubsection?.fields?.[nextFieldIndex];
                    const hasDurationField = nextField && nextField.id === `${field.id}_duration_years`;

                    if (hasDurationField) {
                        const isChecked = value === true || value === 'true' || value === 'YES';
                        const durationValue = data[nextField.id];
                        const durationError = errors[nextField.id];

                        // Create a synthetic linkedField for legacy support
                        const syntheticLinkedField = {
                            id: nextField.id,
                            type: nextField.type,
                            label: nextField.label,
                            validation: nextField.validation
                        };

                        return (
                            <div
                                key={field.id}
                                ref={(el) => setFieldRef(field.id, el)}
                                data-field-id={field.id}
                                id={`field-${field.id}`}
                                className={styles.fieldWidthFull}
                            >
                                <SingleCheckboxInput
                                    question={question}
                                    value={value}
                                    error={error}
                                    isSubmitted={isSubmitted}
                                    onChange={(val) => handleImmediateFieldChange(field.id, val)}
                                    linkedField={syntheticLinkedField}
                                    linkedFieldValue={durationValue}
                                    linkedFieldError={durationError}
                                    onLinkedFieldChange={(val) => handleFieldChange(nextField.id, val)}
                                    onBlur={undefined}
                                    disabled={isViewOnly}
                                />
                            </div>
                        );
                    } else {
                        // Simple checkbox without duration field
                        return fieldWrapper(
                            <SingleCheckboxInput
                                {...commonProps}
                                onBlur={undefined}
                                onChange={(val) => handleImmediateFieldChange(field.id, val)}
                            />
                        );
                    }
                }

            case 'rating':
                return fieldWrapper(
                    <RatingInput
                        {...commonProps}
                        onBlur={undefined}
                        onChange={(val) => handleImmediateFieldChange(field.id, val)}
                    />
                );

            case 'signature':
                return fieldWrapper(
                    <SignatureInput
                        {...commonProps}
                        onChange={(val) => handleSignatureUpload(field.id, val)}
                        onClear={() => handleSignatureDelete(field.id)}
                    />
                );

            case 'calculated':
                return fieldWrapper(
                    <CalculatedInput
                        {...commonProps}
                        allData={data}
                    />
                );

            case 'table':
                return fieldWrapper(
                    <TableInput
                        {...commonProps}
                        onUpdate={(updates) => {
                            Object.keys(updates).forEach(key => {
                                handleFieldChange(key, updates[key]);
                            });
                        }}
                        shouldShow={shouldShow}
                    />,
                    styles.fieldWidthFull
                );

            default:
                return null;
        }
    };
    
    return (
        <div className={styles.container}>
            <div className={styles.formCard}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>{section.title}</h2>
                    {section.description && (
                        <p className={styles.sectionDescription}>{section.description}</p>
                    )}
                    <p className={styles.mandatoryNote}>
                        <span className={styles.required}>section and headers marked with * are mandatory</span> 
                    </p>
                </div>

                {isEligible === false && !isFirstSection && !isLastSection && (
                    <div className={styles.infoAlert}>
                        <div className={styles.alertIcon}>ℹ️</div>
                        <div className={styles.alertContent}>
                            <strong>Subject Excluded</strong>
                            <p>This subject did not meet eligibility criteria. Please proceed to the final declaration section.</p>
                        </div>
                    </div>
                )}

                {validationMessage && !isViewOnly && (
                    <div className={styles.validationAlert}>
                        <span className={styles.alertIcon}>⚠️</span>
                        <span>{validationMessage}</span>
                    </div>
                )}

                <div className={styles.questionsContainer}>
                    {section.subsections && section.subsections.map((subsection, subIndex) => (
                        <div key={subsection.id || subIndex} className={styles.subsection}>
                            {subsection.title && (
                                <h3 className={styles.subsectionTitle}>{subsection.title}</h3>
                            )}
                            {subsection.description && (
                                <p className={styles.subsectionDescription}>{subsection.description}</p>
                            )}
                            <div className={styles.fieldsGrid}>
                                {subsection.fields && subsection.fields.map((field, fieldIndex) => {
                                    if (shouldSkipField(field, subIndex, fieldIndex)) {
                                        return null;
                                    }
                                    return renderField(field, subIndex, fieldIndex);
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <NavigationButtons
                    onBack={onBack}
                    onNext={handleNext}
                    isLastSection={isLastSection}
                    isSubmitting={isSubmitting}
                    isSubmitted={isSubmitted}
                    canSubmitSurvey={!isViewOnly}
                    userRole={userRole}
                    isViewOnly={isViewOnly}
                />
            </div>

            {!isViewOnly && (
                <ConfirmationModal
                    show={showExclusionModal}
                    onConfirm={handleExclusionConfirm}
                    onCancel={handleExclusionCancel}
                    title="Subject Exclusion"
                    message="The subject does not meet the eligibility criteria and will be marked as excluded. Do you want to proceed to the final declaration section?"
                    confirmText="Yes, Proceed to Final Section"
                    cancelText="No, Let Me Review"
                    type="warning"
                />
            )}

            {!isViewOnly && (
                <ConfirmationModal
                    show={showConfirmation}
                    onConfirm={handleConfirmSubmit}
                    onCancel={handleCancelSubmit}
                    title="Confirm Submission"
                    message="Are you sure you want to submit this form? You won't be able to make further changes after submission."
                    confirmText="Yes, Submit"
                    cancelText="No, Review Again"
                    type="warning"
                />
            )}

            {/* Reason for Change Modal */}
            {showReasonModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.reasonModal}>
                        <div className={styles.reasonModalHeader}>
                            <h3 className={styles.reasonModalTitle}>Reason for Change</h3>
                            <button 
                                className={styles.reasonModalClose}
                                onClick={handleReasonCancel}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.reasonModalBody}>
                            <div className={styles.changeInfo}>
                                <p><span className={styles.changeLabel}>New value:</span> <span className={styles.newValue}>{String(pendingChange?.newValue ?? '')}</span></p>
                                <p><span className={styles.changeLabel}>Old Value:</span> <span className={styles.oldValue}>{String(pendingChange?.oldValue ?? '')}</span></p>
                            </div>
                            <input
                                type="text"
                                className={styles.reasonInput}
                                placeholder="Reason"
                                value={changeReason}
                                onChange={(e) => setChangeReason(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <div className={styles.reasonModalFooter}>
                            <button 
                                className={styles.reasonCancelButton}
                                onClick={handleReasonCancel}
                            >
                                CANCEL
                            </button>
                            <button 
                                className={styles.reasonSubmitButton}
                                onClick={handleReasonSubmit}
                                disabled={!changeReason.trim()}
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DynamicFormRenderer;